<template>
  <v-dialog
    v-model="dialogVisible"
    fullscreen
    transition="dialog-bottom-transition"
    data-testid="aggregate-learning-leaderboard-full-dialog"
  >
    <v-card elevation="0">
      <div class="justify-start">
        <Toolbar
          title="Full Leaderboard"
          data-testid="aggregate-learning-leaderboard-full-header"
          color="brandPrimary"
          @close="dialogVisible = false"
        />
      </div>
      <v-row class="justify-center ma-0">
        <v-col cols="12" md="8">
          <div class="leaderboard-container">
            <div>
              <Typography variant="h-4" class="text-left pb-4">
                {{ resolvedTitleText }}
              </Typography>

              <div
                class="d-flex align-center flex-wrap justify-space-between pb-4"
                style="gap: 16px"
              >
                <div class="d-flex align-center flex-wrap" style="gap: 16px">
                  <AggregateLearningLeaderboardSort
                    :value="sortValue"
                    :accent-color="resolvedAccentColor"
                    :disabled="loading"
                    @input="onSortChanged"
                  />

                  <v-menu
                    v-if="hasFilter1"
                    offset-y
                    :disabled="loading"
                    data-testid="aggregate-learning-leaderboard-filter1-menu"
                  >
                    <template #activator="{ on, attrs }">
                      <Button
                        text
                        small
                        icon-right="mdi-menu-down"
                        :color="resolvedAccentColor"
                        :disabled="loading"
                        data-testid="aggregate-learning-leaderboard-filter1-btn"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ filter1ButtonLabel }}
                      </Button>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="option in filter1Options"
                        :key="`filter1-${option.value || 'all'}`"
                        :data-testid="`aggregate-learning-leaderboard-filter1-${
                          option.value || 'all'
                        }`"
                        @click="onFilter1Selected(option)"
                      >
                        <v-list-item-title>{{ option.label }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <v-menu
                    v-if="hasFilter2"
                    offset-y
                    :disabled="loading"
                    data-testid="aggregate-learning-leaderboard-filter2-menu"
                  >
                    <template #activator="{ on, attrs }">
                      <Button
                        text
                        small
                        icon-right="mdi-menu-down"
                        :color="resolvedAccentColor"
                        :disabled="loading"
                        data-testid="aggregate-learning-leaderboard-filter2-btn"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ filter2ButtonLabel }}
                      </Button>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="option in filter2Options"
                        :key="`filter2-${option.value || 'all'}`"
                        :data-testid="`aggregate-learning-leaderboard-filter2-${
                          option.value || 'all'
                        }`"
                        @click="onFilter2Selected(option)"
                      >
                        <v-list-item-title>{{ option.label }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <Button
                  v-if="hasActiveFilters"
                  text
                  small
                  icon-left="mdi-close"
                  :color="resolvedAccentColor"
                  :disabled="loading"
                  data-testid="aggregate-learning-leaderboard-clear-filters-btn"
                  @click="onClearFilters"
                >
                  Clear Filters
                </Button>
              </div>
              <v-data-iterator
                :items="fetchedRows"
                :items-per-page="pageSize"
                :page="currentPage"
                :server-items-length="totalCount"
                :loading="loading"
                :hide-default-footer="totalCount === 0"
                :footer-props="{
                  'disable-pagination': loading,
                  'disable-items-per-page': loading || totalCount <= itemsPerPageOptions[0],
                  'items-per-page-options': itemsPerPageOptions,
                }"
                data-testid="aggregate-learning-leaderboard-full-iterator"
                @update:page="onPageChange"
                @update:items-per-page="onItemsPerPageChange"
              >
                <template v-slot:default="{ items }">
                  <div class="d-flex flex-column" style="gap: 8px">
                    <AggregateLearningLeaderboardRow
                      v-for="(row, index) in items"
                      :key="`${row.rank}-${row.userId || index}`"
                      :rank="row.rank"
                      :user-id="row.userId"
                      :display-name="row.displayName"
                      :initials="row.initials"
                      :avatar-url="row.avatarUrl"
                      :learning-completed="row.learningCompleted"
                      :badges-earned="row.badgesEarned"
                      :is-current-user="row.isCurrentUser"
                      :active-sort="sortValue"
                      :accent-color="resolvedAccentColor"
                      clickable
                      @click="onRowClick"
                    />
                  </div>
                </template>
                <template v-slot:no-data>
                  <div
                    class="text-center py-8 mediumGrey--text"
                    data-testid="aggregate-learning-leaderboard-full-empty"
                  >
                    No leaderboard data is available yet.
                  </div>
                </template>
                <template v-slot:loading>
                  <v-progress-linear indeterminate :color="resolvedAccentColor" class="mb-2" />
                </template>
              </v-data-iterator>
            </div>
          </div>
        </v-col>
      </v-row>

      <AggregateLearningLeaderboardUserProfileSheet
        v-model="showUserProfileSheet"
        :user-id="selectedUserId"
        :accent-color="resolvedAccentColor"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Toolbar from "@/components/generic/Toolbar.vue";
import Button from "@/components/generic/Button.vue";
import AggregateLearningLeaderboardRow from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardRow.vue";
import AggregateLearningLeaderboardSort from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardSort.vue";
import AggregateLearningLeaderboardUserProfileSheet from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardUserProfileSheet.vue";
import activeClientMixin from "@/mixins/activeClientMixin";
import AggregateLeaderboardService from "@/services/AggregateLeaderboardService";

export default {
  name: "AggregateLearningLeaderboardFullView",
  mixins: [activeClientMixin],
  components: {
    Typography,
    Toolbar,
    Button,
    AggregateLearningLeaderboardRow,
    AggregateLearningLeaderboardSort,
    AggregateLearningLeaderboardUserProfileSheet,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    accentColor: {
      type: String,
      default: null,
    },
    titleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      sortValue: "learning",
      filter1Value: null,
      filter2Value: null,
      loading: false,
      fetchedRows: [],
      currentPage: 1,
      pageSize: 100,
      itemsPerPageOptions: [10, 20, 50, 100],
      totalCount: 0,
      clientPrimaryColor: null,
      leaderboardConfig: null,
      leaderboardService: new AggregateLeaderboardService(),
      suppressFilterReload: false,
      showUserProfileSheet: false,
      selectedUserId: null,
      pendingFetch: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    leaderboardClientId() {
      return this.userProfile?.clientId ?? null;
    },
    themeAccentColor() {
      return this.$vuetify?.theme?.themes?.light?.brandPrimary;
    },
    resolvedAccentColor() {
      return this.accentColor || this.clientPrimaryColor || this.themeAccentColor || "#077DAF";
    },
    // Filter config is delivered by GET /aggregate-learning-leaderboard/config/:clientId and
    // is shaped { filter1: { label, userAttribute }, filter1Values: string[], filter2, filter2Values }.
    // Both filters are optional per client, so each `hasFilterN` only renders when the
    // config exposes a label *and* at least one value to choose from.
    filter1Config() {
      return this.leaderboardConfig?.filter1 ?? null;
    },
    filter2Config() {
      return this.leaderboardConfig?.filter2 ?? null;
    },
    filter1Values() {
      return Array.isArray(this.leaderboardConfig?.filter1Values)
        ? this.leaderboardConfig.filter1Values
        : [];
    },
    filter2Values() {
      return Array.isArray(this.leaderboardConfig?.filter2Values)
        ? this.leaderboardConfig.filter2Values
        : [];
    },
    hasFilter1() {
      return !!this.filter1Config?.label && this.filter1Values.length > 0;
    },
    hasFilter2() {
      return !!this.filter2Config?.label && this.filter2Values.length > 0;
    },
    // The first option (value=null) clears the active filter; the rest are the raw
    // string values returned by the config endpoint.
    filter1Options() {
      const label = this.filter1Config?.label || "";
      const allLabel = label ? `All ${label}` : "All";
      return [
        { label: allLabel, value: null },
        ...this.filter1Values.map((v) => ({ label: v, value: v })),
      ];
    },
    filter2Options() {
      const label = this.filter2Config?.label || "";
      const allLabel = label ? `All ${label}` : "All";
      return [
        { label: allLabel, value: null },
        ...this.filter2Values.map((v) => ({ label: v, value: v })),
      ];
    },
    filter1ButtonLabel() {
      return this.filter1Value || this.filter1Config?.label || "";
    },
    filter2ButtonLabel() {
      return this.filter2Value || this.filter2Config?.label || "";
    },
    resolvedTitleText() {
      return this.titleConfig?.text || this.leaderboardConfig?.title?.text || "Leaderboard";
    },
    hasActiveFilters() {
      return this.filter1Value != null || this.filter2Value != null;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.reloadFromFirstPage();
      }
    },
    leaderboardClientId() {
      this.loadClientPrimaryColor();
      this.filter1Value = null;
      this.filter2Value = null;
      this.leaderboardConfig = null;
      this.fetchLeaderboardConfig();
      if (this.value) {
        this.reloadFromFirstPage();
      }
    },
    sortValue() {
      this.reloadFromFirstPage();
    },
    filter1Value() {
      if (this.suppressFilterReload) return;
      this.reloadFromFirstPage();
    },
    filter2Value() {
      if (this.suppressFilterReload) return;
      this.reloadFromFirstPage();
    },
    loading(newLoading) {
      if (newLoading) {
        this.fetchedRows = [];
      }
    },
  },
  async mounted() {
    await this.loadClientPrimaryColor();
    this.fetchLeaderboardConfig();
    if (this.value) {
      this.reloadFromFirstPage();
    }
  },
  methods: {
    onSortChanged(newSortValue) {
      this.sortValue = newSortValue;
    },
    onRowClick(userId) {
      if (!userId) return;
      this.selectedUserId = userId;
      this.showUserProfileSheet = true;
    },
    onFilter1Selected(option) {
      this.filter1Value = option.value;
    },
    onFilter2Selected(option) {
      this.filter2Value = option.value;
    },
    async onClearFilters() {
      if (!this.hasActiveFilters) return;
      // Suppress per-watcher reloads so clearing both filters together only fires one fetch.
      this.suppressFilterReload = true;
      this.filter1Value = null;
      this.filter2Value = null;
      await this.$nextTick();
      this.suppressFilterReload = false;
      this.reloadFromFirstPage();
    },

    async loadClientPrimaryColor() {
      this.clientPrimaryColor = null;
      try {
        const value = await this.getClientField("primaryColor");
        if (value) this.clientPrimaryColor = value;
      } catch (e) {
        console.log("Failed to resolve client primaryColor for leaderboard full view", e);
      }
    },

    // Vuetify's v-data-iterator emits `update:page` and `update:items-per-page`
    // as two separate events when the page size changes. Each handler updates state
    // synchronously and queues a single fetch via `scheduleFetch`, so both events in
    // the same tick collapse to one request against the final state — that's what
    // closes the race where a fetch could go out with a stale pageSize.
    onPageChange(newPage) {
      if (!newPage || newPage === this.currentPage) return;
      this.currentPage = newPage;
      this.scheduleFetch();
    },

    onItemsPerPageChange(newSize) {
      if (!newSize || newSize === this.pageSize) return;
      this.pageSize = newSize;
      this.currentPage = 1;
      this.scheduleFetch();
    },

    scheduleFetch() {
      if (!this.value || this.pendingFetch) return;
      this.pendingFetch = true;
      this.$nextTick(() => {
        this.pendingFetch = false;
        if (this.value) this.fetchFullLeaderboard();
      });
    },

    reloadFromFirstPage() {
      this.currentPage = 1;
      this.scheduleFetch();
    },
    async fetchFullLeaderboard() {
      if (this.leaderboardClientId == null) return;
      this.loading = true;
      try {
        const response = await this.leaderboardService.getFullLeaderboard(
          this.leaderboardClientId,
          {
            sort: this.sortValue,
            filter1: this.filter1Value,
            filter2: this.filter2Value,
            page: this.currentPage,
            limit: this.pageSize,
          }
        );
        this.fetchedRows = Array.isArray(response?.rows) ? response.rows : [];
        this.totalCount = response?.count ?? 0;
      } catch (e) {
        console.log("Failed to fetch full aggregate learning leaderboard", e);
        this.fetchedRows = [];
        this.totalCount = 0;
        this.$store?.dispatch("showError", {
          message: "Failed to load leaderboard. Please try again later.",
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchLeaderboardConfig() {
      if (this.leaderboardClientId == null) return;
      try {
        const response = await this.leaderboardService.getLeaderboardConfig(
          this.leaderboardClientId
        );
        this.leaderboardConfig = response?.result ?? response ?? null;
      } catch (e) {
        console.log("Failed to fetch aggregate learning leaderboard config", e);
        this.leaderboardConfig = null;
      }
    },
  },
};
</script>

<style scoped>
.leaderboard-container {
  padding: 24px;
  overflow-y: auto;
}

@media only screen and (max-width: 600px) {
  .leaderboard-container {
    padding: 8px;
  }
}
</style>
